import { ReactEventHandler, useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";
import { CollectButton, PlayerController, PostButton, Tooltip } from "@/components";
import clsx from "clsx";
import { getCurrentFrame, getOffset, videoUrl2blobUrl, download, getFileName } from "@/utils";
import { useCommonStore } from "@/stores";
import { DownloadSvg } from "@/assets/icons";
import { useTranslation } from "react-i18next";
import {
  COMMUNITY_EXP_TEMP_TITLE_PREFFIX,
  CUS_EXP_TEMP_TITLE_PREFFIX, 
  POSITION, 
  PROJECT_TYPE,
  TaskStatus, 
} from "@/constants";
import FTAnalytic from "@/analyze/FTAnalytic";
import MaiDianUtils from "@/utils/MaiDianUtils";
import { Properties } from "csstype";

type VideoPlayerProps = {
  style?: Properties;
  videoClassName?: string;
  isMute: boolean;
  isPlay: boolean;
  videoSrc: string;
  imgSrc?: string;
  compareVisible?: boolean;
  poster?: string;
  isSample?: boolean;
  setIsMute: (value: boolean) => void;
  setIsPlay: (value: boolean) => void;
  onPlay?: () => void;
  onPause?: () => void;
}

const VideoPlayer = (props: VideoPlayerProps) => {
  const {
    style,
    videoClassName,
    isMute,
    isPlay,
    videoSrc,
    imgSrc,
    compareVisible = false,
    poster,
    isSample,
    setIsMute,
    setIsPlay,
    onPlay,
    onPause,
  } = props;

  const { t } = useTranslation();

  const mediaEleRef = useRef<HTMLDivElement>(null);
  const videoEleRef = useRef<HTMLVideoElement>(null);
  const isCanPlayedRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isCompare, setIsCompare] = useState(false);
  const [position, setPosition] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [videoBlobUrl, setVideoBlobUrl] = useState("");
  const [currentFrame, setCurrentFrame] = useState("");
  const safariInfo = useCommonStore(state => state.safariInfo);
  const task = useCommonStore(state => state.task);

  const calcPosition = () => {
    const videoEle = videoEleRef.current;
    const mediaEle = mediaEleRef.current;
    if (videoEle && mediaEle) {
      const offset = getOffset(videoEle, mediaEle)
      setPosition(offset);
    }
  }

  const getDuration = () => {
    return videoEleRef.current!.duration * 1000;
  }

  const play = () => {
    videoEleRef.current?.play();
  }

  const pause = () => {
    videoEleRef.current?.pause();
  }

  const handlePlay = () => {    
    setIsPlay(true);
    onPlay?.();
  }

  const handlePause = () => {    
    setIsPlay(false);
    onPause?.();
  }

  const handleChangeTime = (time: number) => {    
    videoEleRef.current!.currentTime = time / 1000;
  }

  const handleLoadedMetadata: ReactEventHandler<HTMLVideoElement> = (e) => {
    const videoEle = e.target as HTMLVideoElement;
    setDuration(videoEle.duration);
  }

  const handleLoadedData = () => {
    calcPosition();
  }

  const handleEnd = () => {
    videoEleRef.current!.currentTime = 0;
    play();
  }

  const handleTimeUpdate: ReactEventHandler<HTMLVideoElement> = (e) => {
    setCurrentTime((e.target as HTMLVideoElement).currentTime);
  }

  const handleCanPlay = () => {    
    if (!isCanPlayedRef.current) {
      handlePlay();
      isCanPlayedRef.current = true;
    }
  }

  const handleDownload = () => {
    // 埋点（下载）
    FTAnalytic.addAnalytic({
      type: 'ev',
      action: 'fotor_web$click_up$swap_project_page_download',
      label: {
        position: POSITION,
        button_position: "download",
        project_type: PROJECT_TYPE,
      },
    });

    let is_success = true;
    try {
      const url = task!.videoPath;
      download(url, getFileName(url));
    } catch (err) {
      console.error(err);
      is_success = false;
    }

    let resource_id;
    let resource_name;
    if (MaiDianUtils.expTemp?.id.startsWith(CUS_EXP_TEMP_TITLE_PREFFIX)) {
      resource_id = "customize";
      resource_name = "customize";
    } else if (MaiDianUtils.expTemp?.id.startsWith(COMMUNITY_EXP_TEMP_TITLE_PREFFIX)) {
      const url = new URL(window.location.href);
      resource_id = url.searchParams.get("temp");
      resource_name = "community";
    } else {
      resource_id = MaiDianUtils.expTemp?.id;
      resource_name = MaiDianUtils.expTemp?.title;
    }

    // 埋点（下载结果）
    FTAnalytic.addAnalytic({
      type: 'ev',
      action: 'fotor_web$callback$swap_project_page_download',
      label: {
        position: POSITION,
        is_success,
        button_position: "download",
        project_type: PROJECT_TYPE,
        resource_id,
        resource_name,
      },
    });
  }

  useEffect(() => {
    if (!isSample) {
      (async () => {
        if (videoSrc) {
          try {
            const blobUrl = await videoUrl2blobUrl(videoSrc);
            setVideoBlobUrl(blobUrl);
          } catch (err) {
            console.error(err);
          }
        }
      })()
    }
  }, [videoSrc, isSample]);

  useEffect(() => {    
    if (isPlay) {
      play();
    } else {
      if (safariInfo?.isSafari && videoEleRef.current) {
        getCurrentFrame(videoEleRef.current)
          .then(setCurrentFrame)
          .catch(console.error)
      }
      pause();
    }
  }, [isPlay]);

  useEffect(() => {
    return () => {
      if (currentFrame) {        
        URL.revokeObjectURL(currentFrame);
      }
    }
  }, [currentFrame]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length > 0) {
        calcPosition();
      }
    });
    // 开始监听元素的尺寸变化
    resizeObserver.observe(mediaEleRef.current!);

    return () => {
      resizeObserver.disconnect();
    }
  }, [])

  useEffect(() => {
    return () => {      
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
      }
    }
  }, [videoBlobUrl])

  return (
    <div
      style={style}
      className={styles["video_player"]}
    >
      <i className={styles["placeholder"]}></i>

      <div
        ref={mediaEleRef}
        className={styles["media_wrapper"]}
      >
        {videoBlobUrl || isSample ? (
          <>
            <video 
              style={
                safariInfo?.isSafari 
                ? {  backgroundImage: `url("${currentFrame}")` } 
                : {}
              }
              className={clsx(videoClassName)}
              ref={videoEleRef}
              src={isSample ? videoSrc : videoBlobUrl}
              muted={isMute}
              autoPlay={false}
              loop={false}
              poster={poster}
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={handleLoadedData}
              onTimeUpdate={handleTimeUpdate}
              onContextMenu={e => e.preventDefault()}
              onCanPlay={handleCanPlay}
            ></video>

            {task && task.status === TaskStatus.Success && (
              <div
                style={{
                  left: position.left,
                  right: position.right,
                  top: position.top,
                  bottom: position.bottom,
                }}
                className={styles["operation_wrap"]}
              >
                <div
                  className={styles["right_top_wrap"]}
                >
                  <button
                    className={styles["download_button"]}
                    onClick={handleDownload}
                  >
                    <DownloadSvg></DownloadSvg>

                    <Tooltip
                      content={t("download_button")}
                      placement="left"
                      arrow={false}
                    ></Tooltip>
                  </button>

                  <CollectButton></CollectButton>
                </div>

                <div
                  className={styles["right_bottom_wrap"]}
                >
                  <PostButton></PostButton>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            className={clsx(
              styles["skeleton"],
              "skeleton_loading"
            )}
          ></div>
        )}

        <div
          style={{
            left: position.left,
            right: position.right,
            top: position.top,
            bottom: position.bottom,
          }}
          className={styles["door_frame"]}
        ></div>

        {compareVisible && isCompare && (
          <div
            style={{
              left: position.left,
              right: position.right,
              top: position.top,
              bottom: position.bottom,
            }}
            className={styles["img_wrapper"]}
          >
            <img 
              src={imgSrc}
              alt=""
              onContextMenu={e => e.preventDefault()}
            />
          </div>
        )}
      </div>
      
      <PlayerController
        className={styles["custom_player_controller"]}
        isPlay={isPlay}
        isMute={isMute}
        duration={duration}
        currentTime={currentTime}
        compareVisible={compareVisible}
        onChangeTime={handleChangeTime}
        onPlay={handlePlay}
        onPause={handlePause}
        onMute={() => setIsMute(true)}
        onCancelMute={() => setIsMute(false)}
        onCompare={() => setIsCompare(true)}
        onCancelCompare={() => setIsCompare(false)}
        onEnd={handleEnd}
        getDuration={getDuration}
      ></PlayerController>
    </div>
  )
}

export default VideoPlayer;